import { useParams, useLocation } from "react-router-dom";
import {
  useMaxService,
  useNavigation,
  useLayoutManager,
  useQueryParamProject,
  useProjectCreation,
  useLoggerManager,
  LoggerManager,
} from "@amzn/awsmp-max-agentic-renderer";
import type {
  AppRouteParams,
  AppRouteEphemeralStates,
  NavigationManager,
  LayoutManager,
  ProjectCreationManager,
} from "@amzn/awsmp-max-agentic-renderer-interface";

/**
 * Base page props type - combines all data sources for routes/screens
 * Generic over params type to provide type safety for route parameters
 */
export interface PageProps<TParams extends Record<string, string | undefined> = Record<string, string | undefined>> {
  maxService: ReturnType<typeof useMaxService>;
  projectCreation: ProjectCreationManager;
  navigation: NavigationManager<AppRouteParams, AppRouteEphemeralStates>;
  logger: LoggerManager;
  layout: LayoutManager;
  params: TParams;
  location: ReturnType<typeof useLocation>;
  queryParamProject: ReturnType<typeof useQueryParamProject>;
}

/**
 * Consolidated hook - provides all AgenticRenderer APIs + Router data
 * Generic over params type for type-safe route parameters
 */
export function usePageProps<TParams extends Record<string, string | undefined> = Record<string, string | undefined>>(): PageProps<TParams> {
  const maxService = useMaxService();
  const projectCreation = useProjectCreation();
  const navigation = useNavigation<AppRouteParams, AppRouteEphemeralStates>();
  const logger = useLoggerManager();
  const layout = useLayoutManager();
  const routeParams = useParams();
  const location = useLocation();
  const queryParamProject = useQueryParamProject(location.search);

  return {
    maxService,
    projectCreation,
    navigation,
    logger,
    layout,
    params: routeParams as TParams,
    location,
    queryParamProject,
  };
}
